import { useState, useEffect } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Space+Mono:wght@400;700&family=Syne:wght@400;500;600;700;800&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Mono:wght@400;500&family=Geist:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0a0a0f; --bg2: #111118; --bg3: #1a1a24;
    --border: rgba(255,255,255,0.07); --border2: rgba(255,255,255,0.12);
    --text: #f0f0f8; --text2: #8888aa; --text3: #55556a;
    --accent: #7c6aff; --accent2: #a594ff; --green: #2dd4a0;
    --green-dim: rgba(45,212,160,0.12); --amber: #f59e0b;
    --amber-dim: rgba(245,158,11,0.12); --blue: #60a5fa;
    --purple-glow: rgba(124,106,255,0.15);
  }
  body { background: var(--bg); color: var(--text); font-family: 'Syne', sans-serif; margin: 0; }
  body::before { content: ''; position: fixed; top: -200px; left: -200px; width: 600px; height: 600px; pointer-events: none; z-index: 0; background: radial-gradient(circle, rgba(124,106,255,0.08) 0%, transparent 70%); }
  .shell { display:flex; min-height:100vh; position:relative; z-index:1; }
  .sidebar { width:220px; flex-shrink:0; background:var(--bg2); border-right:1px solid var(--border); display:flex; flex-direction:column; padding:28px 0; position:fixed; top:0; left:0; bottom:0; overflow-y:auto; }
  .logo { padding:0 24px 28px; border-bottom:1px solid var(--border); margin-bottom:20px; }
  .logo-mark { font-family:'Space Mono',monospace; font-size:14px; font-weight:700; color:var(--text); display:flex; align-items:center; gap:8px; }
  .logo-dot { width:8px; height:8px; background:var(--accent); border-radius:50%; box-shadow:0 0 10px var(--accent); animation:pulseDot 2s ease-in-out infinite; flex-shrink:0; }
  @keyframes pulseDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(0.8)} }
  .nav-section { font-size:10px; font-weight:700; letter-spacing:0.1em; color:var(--text3); padding:16px 24px 6px; text-transform:uppercase; }
  .nav-item { display:flex; align-items:center; gap:10px; padding:10px 24px; font-size:13px; font-weight:500; color:var(--text2); cursor:pointer; transition:all 0.15s; border-left:2px solid transparent; margin:1px 0; }
  .nav-item:hover { color:var(--text); background:rgba(255,255,255,0.03); }
  .nav-item.active { color:var(--accent2); border-left-color:var(--accent); background:var(--purple-glow); }
  .sidebar-footer { margin-top:auto; padding:20px 24px; border-top:1px solid var(--border); }
  .avatar-img { width:32px; height:32px; border-radius:50%; object-fit:cover; flex-shrink:0; }
  .avatar-fallback { width:32px; height:32px; border-radius:50%; background:linear-gradient(135deg,var(--accent),var(--green)); display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; color:white; flex-shrink:0; }
  .main { margin-left:220px; flex:1; padding:32px; }
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:28px; flex-wrap:wrap; gap:12px; }
  .page-title { font-size:24px; font-weight:800; letter-spacing:-0.5px; }
  .page-sub { font-size:13px; color:var(--text2); margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:6px; font-family:'Syne',sans-serif; font-size:12px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:all 0.15s; text-decoration:none; }
  .btn-ghost { background:var(--bg3); color:var(--text2); border:1px solid var(--border2); }
  .btn-ghost:hover { color:var(--text); }
  .btn-primary { background:var(--accent); color:white; box-shadow:0 0 20px rgba(124,106,255,0.3); }
  .btn-primary:hover { background:var(--accent2); transform:translateY(-1px); }
  .btn-danger { background:rgba(248,113,113,0.1); color:#f87171; border:1px solid rgba(248,113,113,0.2); }
  .btn-danger:hover { background:rgba(248,113,113,0.2); }
  .btn:disabled { opacity:0.5; cursor:not-allowed; transform:none !important; }
  .stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-bottom:24px; }
  .stats-grid-2 { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; margin-bottom:24px; }
  .stat-card { background:var(--bg2); border:1px solid var(--border); border-radius:14px; padding:20px; position:relative; overflow:hidden; transition:border-color 0.2s,transform 0.2s; animation:fadeUp 0.4s ease both; }
  .stat-card:hover { border-color:var(--border2); transform:translateY(-2px); }
  .stat-card::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; }
  .stat-card.purple::before { background:linear-gradient(90deg,var(--accent),transparent); }
  .stat-card.green::before { background:linear-gradient(90deg,var(--green),transparent); }
  .stat-card.amber::before { background:linear-gradient(90deg,var(--amber),transparent); }
  .stat-card.blue::before { background:linear-gradient(90deg,var(--blue),transparent); }
  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .stat-label { font-size:11px; font-weight:600; color:var(--text3); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:10px; }
  .stat-value { font-family:'Space Mono',monospace; font-size:28px; font-weight:700; line-height:1; margin-bottom:6px; }
  .stat-change { font-size:12px; }
  .stat-change.up { color:var(--green); }
  .stat-change.warn { color:var(--amber); }
  .stat-change.neutral { color:var(--text3); }
  .stat-icon { position:absolute; top:18px; right:18px; font-size:20px; opacity:0.2; }
  .content-grid { display:grid; grid-template-columns:1fr 340px; gap:20px; margin-bottom:20px; }
  .panel { background:var(--bg2); border:1px solid var(--border); border-radius:14px; padding:22px; animation:fadeUp 0.5s ease both; margin-bottom:20px; }
  .panel:last-child { margin-bottom:0; }
  .panel-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .panel-title { font-size:13px; font-weight:700; }
  .panel-badge { font-size:10px; font-weight:700; padding:3px 8px; border-radius:20px; letter-spacing:0.05em; text-transform:uppercase; }
  .badge-green { background:var(--green-dim); color:var(--green); }
  .badge-purple { background:var(--purple-glow); color:var(--accent2); }
  .badge-amber { background:var(--amber-dim); color:var(--amber); }
  .badge-blue { background:rgba(96,165,250,0.1); color:var(--blue); }
  .heatmap-grid { display:grid; grid-template-columns:repeat(26,1fr); gap:3px; margin-bottom:8px; }
  .heatmap-cell { aspect-ratio:1; border-radius:3px; background:var(--bg3); transition:transform 0.1s; cursor:pointer; }
  .heatmap-cell:hover { transform:scale(1.4); }
  .heatmap-cell.l1 { background:rgba(124,106,255,0.2); }
  .heatmap-cell.l2 { background:rgba(124,106,255,0.4); }
  .heatmap-cell.l3 { background:rgba(124,106,255,0.65); }
  .heatmap-cell.l4 { background:rgba(124,106,255,0.9); box-shadow:0 0 6px rgba(124,106,255,0.4); }
  .heatmap-labels { display:flex; justify-content:space-between; }
  .heatmap-label { font-size:10px; color:var(--text3); font-family:'Space Mono',monospace; }
  .day-labels { display:grid; grid-template-columns:repeat(7,1fr); gap:3px; margin-bottom:6px; }
  .day-label { font-size:9px; color:var(--text3); text-align:center; font-family:'Space Mono',monospace; }
  .commit-list { display:flex; flex-direction:column; }
  .commit-item { display:flex; align-items:flex-start; gap:12px; padding:12px 0; border-bottom:1px solid var(--border); }
  .commit-item:last-child { border-bottom:none; }
  .commit-sha { font-family:'Space Mono',monospace; font-size:10px; color:var(--accent2); background:var(--purple-glow); padding:2px 6px; border-radius:4px; flex-shrink:0; margin-top:2px; }
  .commit-msg { font-size:12px; color:var(--text); line-height:1.4; margin-bottom:2px; }
  .commit-meta { font-size:11px; color:var(--text3); }
  .private-tag { font-size:9px; background:rgba(245,158,11,0.15); color:var(--amber); padding:1px 5px; border-radius:3px; margin-left:4px; }
  .ai-tag { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:700; color:var(--accent2); background:var(--purple-glow); padding:3px 8px; border-radius:20px; margin-bottom:10px; }
  .ai-dot { width:5px; height:5px; background:var(--accent); border-radius:50%; animation:pulseDot 1.5s infinite; }
  .standup-body { background:var(--bg3); border-radius:10px; padding:16px; font-size:12px; line-height:1.7; color:var(--text2); margin-bottom:14px; border:1px solid var(--border); min-height:180px; }
  .standup-heading { font-size:11px; font-weight:700; color:var(--text); margin-bottom:4px; }
  .standup-section { margin-bottom:10px; }
  .standup-section:last-child { margin-bottom:0; }
  .burnout-label-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
  .burnout-score { font-family:'Space Mono',monospace; font-size:18px; font-weight:700; }
  .burnout-bar-bg { height:6px; background:var(--bg3); border-radius:20px; overflow:hidden; margin-bottom:6px; }
  .burnout-bar-fill { height:100%; border-radius:20px; background:linear-gradient(90deg,var(--green),var(--amber)); transition:width 1s ease; }
  .repo-list { display:flex; flex-direction:column; gap:8px; }
  .repo-item { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; background:var(--bg3); border-radius:8px; border:1px solid var(--border); transition:border-color 0.15s; cursor:pointer; }
  .repo-item:hover { border-color:var(--border2); }
  .repo-name { font-size:12px; font-weight:600; color:var(--text); font-family:'Space Mono',monospace; }
  .repo-lang { font-size:11px; color:var(--text3); margin-top:2px; }
  .repo-stars { font-family:'Space Mono',monospace; font-size:12px; color:var(--accent2); font-weight:700; }
  .skill-list { display:flex; flex-direction:column; gap:12px; }
  .skill-header { display:flex; justify-content:space-between; margin-bottom:5px; }
  .skill-name { font-size:12px; font-weight:600; }
  .skill-pct { font-family:'Space Mono',monospace; font-size:11px; color:var(--text2); }
  .skill-bar-bg { height:4px; background:var(--bg3); border-radius:20px; overflow:hidden; }
  .skill-bar-fill { height:100%; border-radius:20px; transition:width 1.2s ease; }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
  .cursor { display:inline-block; width:2px; height:12px; background:var(--accent); margin-left:2px; animation:blink 1s infinite; vertical-align:middle; }
  .loading { display:flex; align-items:center; justify-content:center; height:100vh; font-size:14px; color:var(--text2); gap:10px; flex-direction:column; }
  .spinner { width:32px; height:32px; border:2px solid var(--border); border-top-color:var(--accent); border-radius:50%; animation:spin 0.8s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  .error-box { background:rgba(248,113,113,0.1); border:1px solid rgba(248,113,113,0.3); border-radius:10px; padding:14px; font-size:12px; color:#f87171; margin-bottom:16px; }
  .career-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:16px; margin-bottom:20px; }
  .career-card { background:var(--bg3); border:1px solid var(--border); border-radius:12px; padding:18px; }
  .career-card-title { font-size:12px; font-weight:700; color:var(--text); margin-bottom:6px; }
  .career-card-value { font-family:'Space Mono',monospace; font-size:22px; font-weight:700; color:var(--accent2); margin-bottom:4px; }
  .career-card-sub { font-size:11px; color:var(--text3); }
  .integration-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:16px; }
  .integration-card { background:var(--bg3); border:1px solid var(--border); border-radius:12px; padding:18px; display:flex; align-items:center; gap:14px; }
  .integration-icon { font-size:28px; flex-shrink:0; }
  .integration-name { font-size:13px; font-weight:700; color:var(--text); margin-bottom:3px; }
  .integration-desc { font-size:11px; color:var(--text3); }
  .integration-status { margin-left:auto; font-size:10px; font-weight:700; padding:3px 8px; border-radius:20px; flex-shrink:0; }
  .status-connected { background:var(--green-dim); color:var(--green); }
  .status-soon { background:var(--bg2); color:var(--text3); border:1px solid var(--border); }
  .activity-row { display:flex; align-items:flex-start; gap:14px; padding:12px 0; border-bottom:1px solid var(--border); }
  .activity-row:last-child { border-bottom:none; }
  .activity-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; margin-top:4px; }
  .activity-dot.commit { background:var(--accent); }
  .activity-text { font-size:12px; color:var(--text); line-height:1.5; }
  .activity-time { font-size:11px; color:var(--text3); margin-top:2px; }
  .history-item { padding:12px; background:var(--bg3); border-radius:8px; border:1px solid var(--border); margin-bottom:8px; }
  .history-date { font-size:10px; color:var(--text3); font-family:'Space Mono',monospace; margin-bottom:6px; }
  .history-text { font-size:12px; color:var(--text2); line-height:1.5; }
  .repo-selector-page { display:flex; align-items:center; justify-content:center; min-height:100vh; background:var(--bg); padding:20px; }
  .repo-selector-card { background:var(--bg2); border:1px solid var(--border); border-radius:20px; padding:36px 32px; max-width:600px; width:100%; animation:fadeUp 0.5s ease; }
  .repo-selector-title { font-size:20px; font-weight:800; color:var(--text); margin-bottom:6px; }
  .repo-selector-sub { font-size:13px; color:var(--text2); margin-bottom:20px; line-height:1.6; }
  .repo-selector-list { display:flex; flex-direction:column; gap:8px; max-height:380px; overflow-y:auto; margin-bottom:20px; padding-right:4px; }
  .repo-selector-item { display:flex; align-items:center; gap:12px; padding:12px 14px; background:var(--bg3); border-radius:10px; border:2px solid var(--border); cursor:pointer; transition:all 0.15s; }
  .repo-selector-item:hover { border-color:var(--border2); }
  .repo-selector-item.selected { border-color:var(--accent); background:var(--purple-glow); }
  .repo-selector-check { width:20px; height:20px; border-radius:6px; border:2px solid var(--border2); flex-shrink:0; display:flex; align-items:center; justify-content:center; }
  .repo-selector-item.selected .repo-selector-check { background:var(--accent); border-color:var(--accent); }
  .repo-private-badge { font-size:10px; padding:2px 7px; border-radius:4px; background:rgba(245,158,11,0.15); color:var(--amber); font-weight:600; flex-shrink:0; }
  .repo-public-badge { font-size:10px; padding:2px 7px; border-radius:4px; background:var(--green-dim); color:var(--green); font-weight:600; flex-shrink:0; }
  .repo-selector-footer { display:flex; align-items:center; justify-content:space-between; padding-top:16px; border-top:1px solid var(--border); flex-wrap:wrap; gap:10px; }
  .repo-selector-count { font-size:12px; color:var(--text2); }
  .privacy-notice { background:rgba(45,212,160,0.08); border:1px solid rgba(45,212,160,0.2); border-radius:10px; padding:12px 14px; margin-bottom:20px; display:flex; gap:10px; align-items:flex-start; }
  .privacy-notice-text { font-size:12px; color:var(--green); line-height:1.6; }

  /* ===== NEW LANDING PAGE ===== */
  .lp-noise { position:fixed; inset:0; pointer-events:none; z-index:0; opacity:0.025;
    background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size:200px; }
  .lp-orb1 { position:fixed; width:700px; height:700px; border-radius:50%;
    background:radial-gradient(circle, rgba(124,106,255,0.07) 0%, transparent 70%);
    top:-200px; left:-200px; pointer-events:none; z-index:0; }
  .lp-orb2 { position:fixed; width:500px; height:500px; border-radius:50%;
    background:radial-gradient(circle, rgba(45,212,160,0.05) 0%, transparent 70%);
    bottom:-100px; right:-100px; pointer-events:none; z-index:0; }
  .lp-wrap { position:relative; z-index:1; max-width:1100px; margin:0 auto; padding:0 24px; }
  .lp-nav { position:sticky; top:0; z-index:100; backdrop-filter:blur(20px);
    background:rgba(10,10,15,0.85); border-bottom:1px solid var(--border); }
  .lp-nav-inner { display:flex; align-items:center; justify-content:space-between; height:56px; }
  .lp-nav-logo { display:flex; align-items:center; gap:10px; }
  .lp-nav-mark { width:28px; height:28px; background:var(--accent); border-radius:7px;
    display:flex; align-items:center; justify-content:center;
    font-family:'Space Mono',monospace; font-size:11px; font-weight:700; color:white; }
  .lp-nav-text { font-family:'Space Mono',monospace; font-size:14px; font-weight:700; color:var(--text); }
  .lp-nav-badge { font-size:10px; background:rgba(45,212,160,0.12); color:var(--green);
    border:1px solid rgba(45,212,160,0.25); border-radius:20px; padding:3px 10px; }
  .lp-nav-btn { font-family:'Syne',sans-serif; font-size:13px; font-weight:600;
    padding:8px 18px; border-radius:8px; cursor:pointer; transition:all 0.15s; text-decoration:none;
    background:var(--accent); color:white; border:none; box-shadow:0 0 20px rgba(124,106,255,0.3); }
  .lp-nav-btn:hover { background:var(--accent2); transform:translateY(-1px); }
  .lp-hero { padding:100px 0 60px; text-align:center; opacity:0; transform:translateY(24px);
    transition:all 0.7s cubic-bezier(0.16,1,0.3,1); }
  .lp-hero.visible { opacity:1; transform:translateY(0); }
  .lp-eyebrow { display:inline-flex; align-items:center; gap:8px; font-size:12px;
    background:var(--bg2); border:1px solid var(--border2); border-radius:20px;
    padding:6px 14px; color:var(--text2); margin-bottom:32px; font-family:'Space Mono',monospace; }
  .lp-eyebrow-dot { width:6px; height:6px; background:var(--green); border-radius:50%;
    animation:pulseDot 2s infinite; flex-shrink:0; }
  .lp-headline { font-family:'Instrument Serif',serif; font-size:clamp(42px,7vw,76px);
    font-weight:400; line-height:1.08; letter-spacing:-1px; color:var(--text); margin-bottom:24px; }
  .lp-headline em { font-style:italic; color:var(--accent2); }
  .lp-sub { font-size:18px; color:var(--text2); line-height:1.7; max-width:540px;
    margin:0 auto 48px; font-weight:400; font-family:'Geist',sans-serif; }
  .lp-actions { display:flex; align-items:center; justify-content:center; gap:14px; flex-wrap:wrap; margin-bottom:16px; }
  .lp-btn-main { font-family:'Syne',sans-serif; font-size:15px; font-weight:600;
    padding:14px 28px; border-radius:10px; border:none; cursor:pointer; transition:all 0.2s;
    text-decoration:none; display:inline-flex; align-items:center; gap:8px;
    background:white; color:#111; box-shadow:0 0 40px rgba(255,255,255,0.1); }
  .lp-btn-main:hover { background:#f0f0f0; transform:translateY(-2px); }
  .lp-btn-ghost { font-family:'Syne',sans-serif; font-size:15px; font-weight:600;
    padding:14px 28px; border-radius:10px; cursor:pointer; transition:all 0.2s;
    text-decoration:none; display:inline-flex; align-items:center; gap:8px;
    background:transparent; color:var(--text2); border:1px solid var(--border2); }
  .lp-btn-ghost:hover { color:var(--text); background:var(--bg2); }
  .lp-trust { font-size:12px; color:var(--text3); font-family:'Space Mono',monospace; }
  .lp-trust span { color:var(--green); }
  .lp-social-bar { display:flex; align-items:center; justify-content:center; gap:28px;
    padding:16px 0 56px; flex-wrap:wrap; }
  .lp-social-item { display:flex; align-items:center; gap:8px; font-size:12px;
    color:var(--text3); font-family:'Space Mono',monospace; }
  .lp-social-val { color:var(--text2); font-weight:500; }
  .lp-social-div { width:1px; height:16px; background:var(--border2); }
  .lp-demo-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:60px; }
  @media(max-width:680px){ .lp-demo-grid{ grid-template-columns:1fr; } }
  .lp-demo-card { background:var(--bg2); border:1px solid var(--border); border-radius:14px; padding:20px; }
  .lp-demo-label { font-size:10px; font-family:'Space Mono',monospace; color:var(--text3);
    text-transform:uppercase; letter-spacing:0.1em; margin-bottom:14px;
    display:flex; align-items:center; gap:8px; }
  .lp-live-dot { width:5px; height:5px; background:var(--green); border-radius:50%; animation:pulseDot 1.5s infinite; }
  .lp-ai-badge { display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:700;
    color:var(--accent2); background:var(--purple-glow); padding:3px 8px; border-radius:20px;
    font-family:'Space Mono',monospace; margin-bottom:12px; }
  .lp-standup-out { font-size:13px; color:var(--text2); line-height:1.8;
    font-family:'Geist',sans-serif; min-height:80px; }
  .lp-cursor { display:inline-block; width:2px; height:13px; background:var(--accent);
    margin-left:2px; vertical-align:middle; animation:blink 1s infinite; }
  .lp-commit-row { display:flex; align-items:flex-start; gap:10px; padding:10px 0;
    border-bottom:1px solid var(--border); }
  .lp-commit-row:last-child { border-bottom:none; }
  .lp-sha { font-family:'Space Mono',monospace; font-size:10px; color:var(--accent2);
    background:var(--purple-glow); padding:2px 6px; border-radius:4px; flex-shrink:0; margin-top:1px; }
  .lp-commit-msg { font-size:12px; color:var(--text); margin-bottom:2px; }
  .lp-commit-meta { font-size:11px; color:var(--text3); }
  .lp-private-pill { font-size:9px; background:rgba(245,158,11,0.15); color:var(--amber);
    padding:1px 5px; border-radius:3px; margin-left:4px; }
  .lp-burnout-big { font-family:'Space Mono',monospace; font-size:48px; font-weight:700;
    color:var(--green); line-height:1; margin-bottom:6px; }
  .lp-burnout-bar-bg { height:6px; background:var(--bg3); border-radius:20px; overflow:hidden; margin:12px 0 6px; }
  .lp-burnout-bar { height:100%; width:22%; border-radius:20px; background:linear-gradient(90deg,var(--green),var(--amber)); }
  .lp-burnout-lbl { font-size:12px; color:var(--green); font-family:'Space Mono',monospace; }
  .lp-stats-row { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:60px; }
  @media(max-width:600px){ .lp-stats-row{ grid-template-columns:repeat(2,1fr); } }
  .lp-stat-pill { background:var(--bg2); border:1px solid var(--border); border-radius:12px; padding:18px; text-align:center; }
  .lp-stat-val { font-family:'Space Mono',monospace; font-size:20px; font-weight:700; color:var(--text); margin-bottom:4px; }
  .lp-stat-lbl { font-size:11px; color:var(--text3); }
  .lp-features-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:14px; margin-bottom:60px; }
  @media(max-width:600px){ .lp-features-grid{ grid-template-columns:1fr; } }
  .lp-feature-card { background:var(--bg2); border:1px solid var(--border); border-radius:14px;
    padding:24px; transition:border-color 0.2s,transform 0.2s; }
  .lp-feature-card:hover { border-color:var(--border2); transform:translateY(-2px); }
  .lp-feature-icon { font-size:22px; margin-bottom:14px; display:block; }
  .lp-feature-title { font-size:15px; font-weight:700; color:var(--text); margin-bottom:6px; }
  .lp-feature-desc { font-size:13px; color:var(--text2); line-height:1.7; }
  .lp-section-title { font-family:'Instrument Serif',serif; font-size:clamp(26px,4vw,40px);
    font-weight:400; text-align:center; margin-bottom:40px; color:var(--text); }
  .lp-section-title em { font-style:italic; color:var(--accent2); }
  .lp-section-eye { font-size:11px; font-family:'Space Mono',monospace; color:var(--text3);
    text-transform:uppercase; letter-spacing:0.1em; margin-bottom:12px; text-align:center; }
  .lp-cta-card { background:var(--bg2); border:1px solid var(--border2); border-radius:20px;
    padding:56px 48px; text-align:center; position:relative; overflow:hidden; margin-bottom:60px; }
  .lp-cta-card::before { content:''; position:absolute; top:-100px; left:50%; transform:translateX(-50%);
    width:400px; height:400px; border-radius:50%;
    background:radial-gradient(circle, rgba(124,106,255,0.1) 0%, transparent 70%); pointer-events:none; }
  .lp-cta-title { font-family:'Instrument Serif',serif; font-size:clamp(26px,4vw,42px);
    font-weight:400; color:var(--text); margin-bottom:16px; position:relative; }
  .lp-cta-sub { font-size:16px; color:var(--text2); margin-bottom:36px; line-height:1.6;
    position:relative; font-family:'Geist',sans-serif; }
  .lp-avatar-stack { display:flex; align-items:center; justify-content:center; gap:12px; margin-top:24px; }
  .lp-avatars { display:flex; }
  .lp-avatar { width:28px; height:28px; border-radius:50%; border:2px solid var(--bg2);
    margin-left:-8px; font-size:10px; font-weight:700; display:flex; align-items:center;
    justify-content:center; color:white; flex-shrink:0; font-family:'Space Mono',monospace; }
  .lp-avatar:first-child { margin-left:0; }
  .lp-avatar-text { font-size:12px; color:var(--text3); font-family:'Space Mono',monospace; }
  .lp-footer { border-top:1px solid var(--border); padding:32px 0; }
  .lp-footer-inner { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; }
  .lp-footer-text { font-size:12px; color:var(--text3); font-family:'Space Mono',monospace; }
  .lp-footer-links { display:flex; gap:20px; }
  .lp-footer-link { font-size:12px; color:var(--text3); text-decoration:none; font-family:'Space Mono',monospace; transition:color 0.15s; }
  .lp-footer-link:hover { color:var(--text2); }
  @media(max-width:500px){
    .lp-hero{ padding:60px 0 40px; }
    .lp-cta-card{ padding:36px 24px; }
    .lp-nav-badge{ display:none; }
  }
`;

const API = "https://devpulse-api-egdtdnevcme9gueg.austriaeast-01.azurewebsites.net";
const LOGIN_URL = `${API}/auth/login`;

const heatmapData = [
  0,0,0,1,0,1,2,0,0,1,0,0,2,1,0,0,1,3,0,1,0,2,0,1,4,4,
  0,0,1,0,2,1,0,0,0,1,2,0,0,1,0,0,2,1,3,0,1,0,2,4,1,0,
  0,1,0,2,0,0,1,2,3,0,1,0,0,2,0,1,0,0,1,2,0,0,4,1,0,2,
  1,0,0,1,0,2,0,0,1,0,3,2,0,1,0,0,2,1,0,0,1,2,0,1,0,0,
  0,0,1,0,2,1,0,0,0,1,2,0,3,1,0,0,2,1,0,0,1,0,4,4,3,2,
  0,1,0,2,0,0,1,2,0,0,1,0,0,2,0,1,0,0,1,2,0,0,1,0,0,2,
  1,0,0,1,0,2,0,3,1,0,0,2,1,0,4,1,2,0,0,1,2,4,4,4,4,4
];

const skillColors = [
  "linear-gradient(90deg,#f59e0b,#fbbf24)",
  "linear-gradient(90deg,#7c6aff,#a594ff)",
  "linear-gradient(90deg,#f87171,#fca5a5)",
  "linear-gradient(90deg,#2dd4a0,#6ee7b7)",
];

const getBurnoutColor = (s) => s >= 70 ? "#f87171" : s >= 40 ? "var(--amber)" : "var(--green)";
const getBurnoutLabel = (s) => s >= 70 ? "🔴 High — take a break!" : s >= 40 ? "🟡 Medium — watch your pace" : "🟢 Very Low — great pace";
const getHour = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };
const getToken = () => localStorage.getItem('devpulse_session');
const authFetch = (url, options = {}) => {
  const token = getToken();
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      ...(options.headers || {}),
      ...(token ? { 'X-Session-Token': token } : {}),
      ...(options.body ? { 'Content-Type': 'application/json' } : {})
    }
  });
};

// ===== EMAIL CAPTURE COMPONENT =====
function EmailCapture({ onComplete }) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }
    setSaving(true);
    try {
      await authFetch(`${API}/auth/save-email`, {
        method: "POST",
        body: JSON.stringify({ email })
      });
      onComplete();
    } catch (e) {
      setError("Something went wrong — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:"var(--bg)",padding:20}}>
      <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:20,padding:"40px 36px",maxWidth:440,width:"100%",animation:"fadeUp 0.5s ease"}}>
        <div style={{fontSize:32,marginBottom:16}}>👋</div>
        <div style={{fontSize:22,fontWeight:700,marginBottom:8,color:"var(--text)",fontFamily:"Space Mono,monospace"}}>
          One last thing
        </div>
        <div style={{fontSize:13,color:"var(--text2)",marginBottom:28,lineHeight:1.7}}>
          Your GitHub email is private — totally fine! Drop your email below so we can send your daily standup and burnout alerts.
        </div>
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          style={{
            width:"100%",padding:"13px 14px",borderRadius:10,
            border:`1px solid ${error ? "#f87171" : "var(--border2)"}`,
            background:"var(--bg3)",color:"var(--text)",fontSize:14,
            marginBottom:error ? 6 : 12,outline:"none",boxSizing:"border-box",
            fontFamily:"Geist,sans-serif"
          }}
        />
        {error && <div style={{fontSize:12,color:"#f87171",marginBottom:12}}>{error}</div>}
        <button
          onClick={handleSubmit}
          disabled={saving || !email.includes("@")}
          style={{
            width:"100%",padding:"13px",borderRadius:10,
            background:saving?"var(--bg3)":"var(--accent)",
            color:"white",border:"none",fontSize:14,fontWeight:600,
            cursor:saving?"not-allowed":"pointer",transition:"all 0.15s",
            fontFamily:"Syne,sans-serif"
          }}
        >
          {saving ? "Saving..." : "Continue to dashboard →"}
        </button>
        <div style={{fontSize:11,color:"var(--text3)",textAlign:"center",marginTop:12,lineHeight:1.6}}>
          Only used for your standup and burnout alerts. No spam ever.
        </div>
      </div>
    </div>
  );
}

// ===== LANDING PAGE =====
function LandingPage() {
  const [visible, setVisible] = useState(false);
  const [standupText, setStandupText] = useState("");
  const [typing, setTyping] = useState(false);
  const fullText = "Yesterday I fixed the auth token refresh bug on mobile and added the burnout score API endpoint. Today I'll be working on the dashboard heatmap redesign. No blockers.";

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    setTimeout(() => {
      setTyping(true);
      let i = 0;
      const interval = setInterval(() => {
        setStandupText(fullText.slice(0, i));
        i++;
        if (i > fullText.length) clearInterval(interval);
      }, 22);
      return () => clearInterval(interval);
    }, 1200);
  }, []);

  const demoCommits = [
    { sha: "a3f2c1", msg: "Fix auth token refresh on mobile", repo: "devpulse", time: "2h ago", priv: true },
    { sha: "b7e910", msg: "Add burnout score endpoint", repo: "api-core", time: "4h ago", priv: true },
    { sha: "c12d45", msg: "Update dashboard heatmap colours", repo: "devpulse", time: "yesterday", priv: false },
  ];

  return (
    <>
      <div className="lp-noise" />
      <div className="lp-orb1" />
      <div className="lp-orb2" />
      <nav className="lp-nav">
        <div className="lp-wrap">
          <div className="lp-nav-inner">
            <div className="lp-nav-logo">
              <div className="lp-nav-mark">{"</>"}</div>
              <span className="lp-nav-text">CodexPulse</span>
              <span className="lp-nav-badge">● Live</span>
            </div>
            <a href={LOGIN_URL} className="lp-nav-btn">Start free →</a>
          </div>
        </div>
      </nav>
      <div className="lp-wrap">
        <div className={`lp-hero ${visible ? "visible" : ""}`}>
          <div className="lp-eyebrow">
            <div className="lp-eyebrow-dot" />
            Built for developers who ship — not just talk
          </div>
          <h1 className="lp-headline">
            Your GitHub already<br />knows if you're <em>burning out</em>
          </h1>
          <p className="lp-sub">
            CodexPulse reads your commits, writes your daily standup, and scores your burnout risk — so you see what's coming before it hits.
          </p>
          <div className="lp-actions">
            <a href={LOGIN_URL} className="lp-btn-main">
              <span style={{fontSize:18}}>🐙</span> Connect GitHub — it's free
            </a>
            <a href="#demo" className="lp-btn-ghost">See how it works ↓</a>
          </div>
          <div className="lp-trust">
            <span>✓ 30-day Pro trial</span> · No credit card · Private repos stay private
          </div>
        </div>
        <div className="lp-social-bar">
          {[
            {val:"GitHub OAuth", label:"one-click login"},
            {val:"30-day", label:"free Pro trial"},
            {val:"100% private", label:"your data stays yours"},
            {val:"Unlimited", label:"standups on Pro"},
          ].map((item, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:28}}>
              {i > 0 && <div className="lp-social-div" />}
              <div className="lp-social-item">
                <span className="lp-social-val">{item.val}</span>
                <span>{item.label}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="lp-demo-grid" id="demo">
          <div className="lp-demo-card">
            <div className="lp-demo-label"><div className="lp-live-dot" /> Your commits</div>
            {demoCommits.map((c, i) => (
              <div className="lp-commit-row" key={i}>
                <span className="lp-sha">{c.sha}</span>
                <div>
                  <div className="lp-commit-msg">{c.msg}{c.priv && <span className="lp-private-pill">🔒</span>}</div>
                  <div className="lp-commit-meta">{c.repo} · {c.time}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="lp-demo-card">
            <div className="lp-demo-label">AI standup — generated just now</div>
            <div className="lp-ai-badge"><div className="lp-live-dot" /> Claude AI</div>
            <div className="lp-standup-out">
              {standupText}
              {typing && standupText.length < fullText.length && <span className="lp-cursor" />}
            </div>
          </div>
          <div className="lp-demo-card">
            <div className="lp-demo-label"><div className="lp-live-dot" /> Burnout risk score</div>
            <div className="lp-burnout-big">22</div>
            <div className="lp-burnout-bar-bg"><div className="lp-burnout-bar" /></div>
            <div className="lp-burnout-lbl">🟢 Very low — great pace</div>
            <div style={{fontSize:12,color:"var(--text3)",marginTop:12,lineHeight:1.6}}>
              Based on commit frequency, session length, and context switching across your repos.
            </div>
          </div>
          <div className="lp-demo-card">
            <div className="lp-demo-label">Career health</div>
            {[
              {label:"Active repos", val:"11", color:"var(--accent2)"},
              {label:"Top language", val:"JavaScript", color:"var(--amber)"},
              {label:"Commits this week", val:"17", color:"var(--green)"},
              {label:"Career momentum", val:"↑ Strong", color:"var(--green)"},
            ].map((row, i) => (
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:i<3?"1px solid var(--border)":"none"}}>
                <span style={{fontSize:12,color:"var(--text2)",fontFamily:"'Geist',sans-serif"}}>{row.label}</span>
                <span style={{fontSize:13,fontFamily:"'Space Mono',monospace",fontWeight:700,color:row.color}}>{row.val}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-stats-row">
          {[
            {val:"~10 min", label:"saved every morning"},
            {val:"Free", label:"to start, no card"},
            {val:"30 days", label:"free Pro trial"},
            {val:"100%", label:"private & secure"},
          ].map((s, i) => (
            <div className="lp-stat-pill" key={i}>
              <div className="lp-stat-val">{s.val}</div>
              <div className="lp-stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="lp-section-eye">What it does</div>
        <h2 className="lp-section-title">Everything your career <em>dashboard</em> should be</h2>
        <div className="lp-features-grid">
          {[
            {icon:"◈", title:"AI standup in seconds", desc:"Claude reads your commits and writes your standup every morning. Copy, paste, done.", color:"var(--accent2)"},
            {icon:"♡", title:"Burnout detection", desc:"Spots overwork patterns weeks before burnout hits — so you can act before it's too late.", color:"var(--green)"},
            {icon:"↑", title:"Career health score", desc:"See your skills, velocity, and growth compounding across every repo you've ever touched.", color:"var(--amber)"},
            {icon:"🔒", title:"Private by design", desc:"Your private repos stay yours. Your data never goes to your employer. Ever.", color:"var(--blue)"},
          ].map((f, i) => (
            <div className="lp-feature-card" key={i}>
              <span className="lp-feature-icon" style={{color:f.color}}>{f.icon}</span>
              <div className="lp-feature-title">{f.title}</div>
              <div className="lp-feature-desc">{f.desc}</div>
            </div>
          ))}
        </div>
        <div className="lp-cta-card">
          <h2 className="lp-cta-title">Start before you burn out</h2>
          <p className="lp-cta-sub">Connect your GitHub in 30 seconds. Your first standup is ready before your morning coffee.</p>
          <a href={LOGIN_URL} className="lp-btn-main">
            <span style={{fontSize:18}}>🐙</span> Connect GitHub — free for 30 days
          </a>
          <div className="lp-avatar-stack">
            <div className="lp-avatars">
              {[{i:"SB",c:"#7c6aff"},{i:"JD",c:"#2dd4a0"},{i:"MK",c:"#f59e0b"},{i:"AL",c:"#f87171"},{i:"RK",c:"#60a5fa"}].map((a,idx)=>(
                <div className="lp-avatar" key={idx} style={{background:a.c}}>{a.i}</div>
              ))}
            </div>
            <span className="lp-avatar-text">Join developers already on CodexPulse</span>
          </div>
        </div>
      </div>
      <footer className="lp-footer">
        <div className="lp-wrap">
          <div className="lp-footer-inner">
            <span className="lp-footer-text">© 2026 CodexPulse · Built with Claude AI · Hosted on Azure</span>
            <div className="lp-footer-links">
              <a href="/privacy" className="lp-footer-link">Privacy</a>
              <a href="/terms" className="lp-footer-link">Terms</a>
              <a href="mailto:noreply@codexpulse.io" className="lp-footer-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default function App() {
  const [stats, setStats] = useState(null);
  const [standup, setStandup] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [standupLoading, setStandupLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skillWidths, setSkillWidths] = useState([]);
  const [burnoutWidth, setBurnoutWidth] = useState(0);
  const [activeNav, setActiveNav] = useState("Dashboard");
  const [standupHistory, setStandupHistory] = useState([]);
  const [showRepoSelector, setShowRepoSelector] = useState(false);
  const [availableRepos, setAvailableRepos] = useState([]);
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [repoSaving, setRepoSaving] = useState(false);

  const checkAuth = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionToken = urlParams.get('session');
      if (sessionToken) {
        localStorage.setItem('devpulse_session', sessionToken);
        window.history.replaceState({}, '', '/');
      }
      const token = getToken();
      if (!token) return false;
      const res = await authFetch(`${API}/auth/me`);
      if (res.ok) {
        const data = await res.json();
        setAuthUser(data);
        return true;
      } else {
        localStorage.removeItem('devpulse_session');
      }
    } catch {}
    return false;
  };

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const [profileRes, statsRes] = await Promise.all([
        authFetch(`${API}/api/profile`),
        authFetch(`${API}/api/stats`),
      ]);
      const profileData = await profileRes.json();
      const statsData = await statsRes.json();
      setProfile(profileData);
      setStats(statsData);
      setSelectedRepos(statsData.selected_repos || []);
      setTimeout(() => {
        setBurnoutWidth(statsData.burnout_score || 10);
        const langs = Object.entries(statsData.languages || {});
        const total = langs.reduce((a,b)=>a+b[1],0)||1;
        setSkillWidths(langs.map(([,v])=>Math.round((v/total)*100)));
      }, 500);
    } catch { setError("Could not connect to CodexPulse backend."); }
    finally { setLoading(false); }
  };

  const fetchStandup = async () => {
    try {
      setStandupLoading(true);
      const res = await authFetch(`${API}/api/standup`);
      const data = await res.json();
      setStandup(data);
    } catch { setStandup({did:"Worked on multiple repository updates.",today:"Continuing development.",blockers:"No blockers."}); }
    finally { setStandupLoading(false); }
  };

  const fetchStandupHistory = async () => {
    try {
      const res = await authFetch(`${API}/api/standup/history`);
      const data = await res.json();
      setStandupHistory(data.history || []);
    } catch {}
  };

  const fetchAvailableRepos = async () => {
    try {
      const res = await authFetch(`${API}/auth/repos`);
      const data = await res.json();
      setAvailableRepos(data.repos || []);
    } catch {}
  };

  const toggleRepo = (repoName) => {
    setSelectedRepos(prev => prev.includes(repoName) ? prev.filter(r=>r!==repoName) : [...prev, repoName]);
  };

  const saveRepoSelection = async () => {
    try {
      setRepoSaving(true);
      await authFetch(`${API}/auth/repos/save`, {
        method:"POST",
        body: JSON.stringify({repos: selectedRepos})
      });
      setShowRepoSelector(false);
      await fetchData();
    } catch {} finally { setRepoSaving(false); }
  };

  const handleLogout = async () => {
    try { await authFetch(`${API}/auth/logout`, {method:"POST"}); } catch {}
    localStorage.removeItem('devpulse_session');
    setAuthUser(null); setStats(null); setProfile(null); setStandup(null);
  };

  useEffect(() => {
    const init = async () => {
      const loggedIn = await checkAuth();
      setAuthChecked(true);
      if (loggedIn) {
        await fetchData();
        await fetchStandup();
        await fetchStandupHistory();
      }
    };
    init();
  }, []);

  const navGroups = [
    {section:"Overview", items:[{icon:"▦",label:"Dashboard"},{icon:"◈",label:"Activity"},{icon:"◎",label:"Repos"}]},
    {section:"AI Features", items:[{icon:"✦",label:"Standup"},{icon:"♡",label:"Wellbeing"},{icon:"↑",label:"Career"}]},
    {section:"Settings", items:[{icon:"⊞",label:"Integrations"},{icon:"◉",label:"Settings"}]},
  ];

  const getPlanColor = () => {
    if (authUser?.plan === "pro") return "#2dd4a0";
    if (authUser?.plan === "trial") return "#f59e0b";
    return "var(--text3)";
  };

  const getPlanLabel = () => {
    if (authUser?.plan === "pro") return "Pro Plan · $12/mo";
    if (authUser?.plan === "trial") return `Trial · ${authUser?.days_left || 30} days left`;
    return "Free Plan — upgrade";
  };

  if (!authChecked) return (
    <><style>{styles}</style>
    <div className="loading"><div className="spinner"/><div style={{fontFamily:'Geist,sans-serif'}}>Loading CodexPulse...</div></div></>
  );

  if (!authUser) return <><style>{styles}</style><LandingPage /></>;

  if (authUser && authUser.needs_email) return (
    <><style>{styles}</style><EmailCapture onComplete={() => setAuthUser({...authUser, needs_email: false})} /></>
  );

  if (showRepoSelector) return (
    <>
      <style>{styles}</style>
      <div className="repo-selector-page">
        <div className="repo-selector-card">
          <div style={{fontSize:28,marginBottom:12}}>🔒</div>
          <div className="repo-selector-title">Choose Your Repositories</div>
          <div className="repo-selector-sub">Select which repos CodexPulse can analyse. Your private repos are never shared with anyone.</div>
          <div className="privacy-notice">
            <span style={{fontSize:18,flexShrink:0}}>🛡️</span>
            <div className="privacy-notice-text"><strong>Private by design.</strong> CodexPulse reads your commit activity only for your personal dashboard. We never share it with employers.</div>
          </div>
          {availableRepos.length===0 ? (
            <div style={{display:"flex",justifyContent:"center",padding:20}}><div className="spinner"/></div>
          ) : (
            <div className="repo-selector-list">
              {availableRepos.map((repo,i)=>(
                <div key={i} className={`repo-selector-item ${selectedRepos.includes(repo.name)?"selected":""}`} onClick={()=>toggleRepo(repo.name)}>
                  <div className="repo-selector-check">{selectedRepos.includes(repo.name)&&<span style={{color:"white",fontSize:12}}>✓</span>}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)",fontFamily:"Space Mono,monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{repo.name}</div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{repo.language||"Unknown"} · Updated {repo.updated}</div>
                  </div>
                  <span className={repo.private?"repo-private-badge":"repo-public-badge"}>{repo.private?"🔒 Private":"Public"}</span>
                </div>
              ))}
            </div>
          )}
          <div className="repo-selector-footer">
            <div className="repo-selector-count">{selectedRepos.length} of {availableRepos.length} repos selected</div>
            <div style={{display:"flex",gap:10}}>
              <button className="btn btn-ghost" onClick={()=>setShowRepoSelector(false)}>Skip for now</button>
              <button className="btn btn-primary" onClick={saveRepoSelection} disabled={selectedRepos.length===0||repoSaving}>
                {repoSaving?"Saving...":`Analyse ${selectedRepos.length} repos →`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (loading) return (
    <><style>{styles}</style>
    <div className="loading"><div className="spinner"/><div style={{fontFamily:'Geist,sans-serif'}}>Loading your developer pulse...</div></div></>
  );

  const username = profile?.username || authUser?.username || "developer";
  const langs = Object.entries(stats?.languages || {});

  const PageDashboard = () => (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{getHour()}, {username.charAt(0).toUpperCase()+username.slice(1)} 👋</div>
          <div className="page-sub">{new Date().toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'long',year:'numeric'})} · Here's your developer pulse</div>
        </div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <button className="btn btn-ghost" onClick={fetchData}>⟳ Sync GitHub</button>
          <button className="btn btn-ghost" onClick={async()=>{await fetchAvailableRepos();setShowRepoSelector(true);}}>🔒 Repos</button>
          <button className="btn btn-primary" onClick={fetchStandup} disabled={standupLoading}>{standupLoading?"⟳ Generating...":"✦ Generate Standup"}</button>
        </div>
      </div>
      {selectedRepos.length>0&&(
        <div style={{background:"rgba(45,212,160,0.08)",border:"1px solid rgba(45,212,160,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"var(--green)",display:"flex",alignItems:"center",gap:8}}>
          🔒 Analysing {selectedRepos.length} selected repo{selectedRepos.length>1?"s":""}: {selectedRepos.join(", ")}
          <button className="btn btn-ghost" style={{marginLeft:"auto",fontSize:11,padding:"3px 10px"}} onClick={async()=>{await fetchAvailableRepos();setShowRepoSelector(true);}}>Change</button>
        </div>
      )}
      {authUser?.plan==="trial"&&(
        <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"var(--amber)",display:"flex",alignItems:"center",gap:8}}>
          ⏱ Pro trial ends in <strong style={{margin:"0 4px"}}>{authUser?.days_left||30} days</strong> — upgrade to keep unlimited standups and full history
          <button className="btn btn-ghost" style={{marginLeft:"auto",fontSize:11,padding:"3px 10px",borderColor:"var(--amber)",color:"var(--amber)"}}>Upgrade →</button>
        </div>
      )}
      <div className="stats-grid">
        {[
          {cls:"purple",label:"Commits This Week",value:stats?.commit_count||0,change:`↑ Active across ${stats?.repos?.length||0} repos`,type:"up",icon:"◈"},
          {cls:"green",label:"Burnout Risk",value:stats?.burnout_score||0,sub:"/100",change:getBurnoutLabel(stats?.burnout_score||0),type:"up",icon:"♡"},
          {cls:"amber",label:"Active Repos",value:stats?.total_repos||0,change:`${stats?.repos?.length||0} updated recently`,type:"warn",icon:"◎"},
          {cls:"blue",label:"Top Language",value:stats?.top_language||"—",change:Object.keys(stats?.languages||{}).join(" · ")||"—",type:"neutral",icon:"{}"},
        ].map((s,i)=>(
          <div key={i} className={`stat-card ${s.cls}`} style={{animationDelay:`${i*0.05}s`}}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{fontSize:typeof s.value==='string'&&s.value.length>6?18:28}}>
              {s.value}{s.sub&&<span style={{fontSize:16,color:"var(--text3)"}}>{s.sub}</span>}
            </div>
            <div className={`stat-change ${s.type}`}>{s.change}</div>
            <div className="stat-icon">{s.icon}</div>
          </div>
        ))}
      </div>
      <div className="content-grid">
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Commit Activity — Last 6 Months</div><span className="panel-badge badge-purple">182 days</span></div>
            <div className="day-labels">{["M","T","W","T","F","S","S"].map((d,i)=><div key={i} className="day-label">{d}</div>)}</div>
            <div className="heatmap-grid">{heatmapData.map((l,i)=><div key={i} className={`heatmap-cell${l>0?` l${l}`:""}`}/>)}</div>
            <div className="heatmap-labels"><span className="heatmap-label">Nov 2025</span><span className="heatmap-label">Today</span></div>
          </div>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Recent Commits</div><span className="panel-badge badge-green">Live</span></div>
            <div className="commit-list">
              {(stats?.commits||[]).map((c,i)=>(
                <div key={i} className="commit-item">
                  <span className="commit-sha">{c.sha}</span>
                  <div><div className="commit-msg">{c.message}{c.private&&<span className="private-tag">🔒</span>}</div><div className="commit-meta">{c.repo} · {c.date}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">AI Standup</div><span className="panel-badge badge-purple">✦ Claude AI</span></div>
            <div className="ai-tag"><div className="ai-dot"/> Generated from your commits</div>
            <div className="standup-body">
              {standupLoading?(
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",gap:8,color:"var(--text3)"}}>
                  <div className="spinner" style={{width:20,height:20}}/> Generating...
                </div>
              ):standup?(
                <>
                  <div className="standup-section"><div className="standup-heading">✅ What I did</div><div>{standup.did}</div></div>
                  <div className="standup-section"><div className="standup-heading">🔨 Working on today</div><div>{standup.today}</div></div>
                  <div className="standup-section"><div className="standup-heading">🚧 Blockers</div><div>{standup.blockers}<span className="cursor"/></div></div>
                </>
              ):<div style={{color:"var(--text3)",fontSize:12}}>Click Generate Standup above</div>}
            </div>
            <button className="btn btn-ghost" style={{width:"100%",justifyContent:"center"}} onClick={fetchStandup} disabled={standupLoading}>
              {standupLoading?"⟳ Generating...":"⟳ Regenerate Standup"}
            </button>
          </div>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Wellbeing Score</div><span className="panel-badge badge-green">🟢 Healthy</span></div>
            <div>
              <div className="burnout-label-row">
                <span style={{fontSize:12,color:"var(--text2)"}}>Burnout Risk</span>
                <span className="burnout-score" style={{color:getBurnoutColor(stats?.burnout_score||0)}}>{stats?.burnout_score||0}</span>
              </div>
              <div className="burnout-bar-bg"><div className="burnout-bar-fill" style={{width:`${burnoutWidth}%`}}/></div>
              <div style={{fontSize:11,color:getBurnoutColor(stats?.burnout_score||0),fontWeight:600}}>{getBurnoutLabel(stats?.burnout_score||0)}</div>
            </div>
            {langs.length>0&&(
              <div style={{marginTop:16,paddingTop:16,borderTop:"1px solid var(--border)"}}>
                <div className="panel-title" style={{marginBottom:12}}>Language Breakdown</div>
                <div className="skill-list">
                  {langs.map(([lang,count],i)=>{
                    const total=langs.reduce((a,b)=>a+b[1],0)||1;
                    const pct=Math.round((count/total)*100);
                    return(<div key={i}><div className="skill-header"><span className="skill-name">{lang}</span><span className="skill-pct">{pct}%</span></div><div className="skill-bar-bg"><div className="skill-bar-fill" style={{width:`${skillWidths[i]||0}%`,background:skillColors[i%skillColors.length]}}/></div></div>);
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Top Repositories</div><span className="panel-badge badge-amber">{stats?.total_repos||0} total</span></div>
            <div className="repo-list">
              {(stats?.repos||[]).map((r,i)=>(
                <div key={i} className="repo-item">
                  <div><div className="repo-name">{r.name}{r.private&&<span className="private-tag">🔒</span>}</div><div className="repo-lang">{r.language||"Unknown"} · {r.updated}</div></div>
                  <div className="repo-stars">⭐ {r.stars}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const PageActivity = () => (
    <>
      <div className="page-header"><div><div className="page-title">Activity</div><div className="page-sub">Your full commit and repo timeline</div></div></div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Commit Heatmap</div><span className="panel-badge badge-purple">182 days</span></div>
        <div className="day-labels">{["M","T","W","T","F","S","S"].map((d,i)=><div key={i} className="day-label">{d}</div>)}</div>
        <div className="heatmap-grid">{heatmapData.map((l,i)=><div key={i} className={`heatmap-cell${l>0?` l${l}`:""}`}/>)}</div>
        <div className="heatmap-labels"><span className="heatmap-label">Nov 2025</span><span className="heatmap-label">Today</span></div>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Recent Activity</div><span className="panel-badge badge-green">Live</span></div>
        {(stats?.commits||[]).map((c,i)=>(
          <div key={i} className="activity-row">
            <div className="activity-dot commit"/>
            <div><div className="activity-text">Committed to <strong>{c.repo}</strong>{c.private&&<span className="private-tag">🔒</span>} — {c.message}</div><div className="activity-time">{c.date} · #{c.sha}</div></div>
          </div>
        ))}
      </div>
    </>
  );

  const PageRepos = () => (
    <>
      <div className="page-header">
        <div><div className="page-title">Repositories</div><div className="page-sub">All {stats?.total_repos||0} of your GitHub repos</div></div>
        <div style={{display:"flex",gap:10}}>
          <button className="btn btn-ghost" onClick={async()=>{await fetchAvailableRepos();setShowRepoSelector(true);}}>🔒 Manage Repos</button>
          <a href={`https://github.com/${username}`} target="_blank" rel="noreferrer"><button className="btn btn-ghost">Open GitHub ↗</button></a>
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Active Repositories</div><span className="panel-badge badge-amber">{stats?.repos?.length||0} repos</span></div>
        <div className="repo-list">
          {(stats?.repos||[]).map((r,i)=>(
            <div key={i} className="repo-item">
              <div><div className="repo-name">{r.name}{r.private&&<span className="private-tag">🔒</span>}</div><div className="repo-lang">{r.language||"Unknown"} · Updated {r.updated}</div></div>
              <div className="repo-stars">⭐ {r.stars}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Language Breakdown</div><span className="panel-badge badge-purple">From your repos</span></div>
        <div className="skill-list">
          {langs.map(([lang,count],i)=>{
            const total=langs.reduce((a,b)=>a+b[1],0)||1;
            const pct=Math.round((count/total)*100);
            return(<div key={i}><div className="skill-header"><span className="skill-name">{lang}</span><span className="skill-pct">{pct}%</span></div><div className="skill-bar-bg"><div className="skill-bar-fill" style={{width:`${skillWidths[i]||0}%`,background:skillColors[i%skillColors.length]}}/></div></div>);
          })}
        </div>
      </div>
    </>
  );

  const PageStandup = () => (
    <>
      <div className="page-header">
        <div><div className="page-title">AI Standup</div><div className="page-sub">Claude AI generates your daily standup from your commits</div></div>
        <button className="btn btn-primary" onClick={fetchStandup} disabled={standupLoading}>{standupLoading?"⟳ Generating...":"✦ Generate New"}</button>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Today's Standup</div><span className="panel-badge badge-purple">✦ Claude AI</span></div>
        <div className="ai-tag"><div className="ai-dot"/> Generated from your real commits</div>
        <div className="standup-body" style={{minHeight:220}}>
          {standupLoading?(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:180,gap:8,color:"var(--text3)"}}>
              <div className="spinner" style={{width:20,height:20}}/> Claude AI is reading your commits...
            </div>
          ):standup?(
            <>
              <div className="standup-section"><div className="standup-heading">✅ What I did</div><div>{standup.did}</div></div>
              <div className="standup-section"><div className="standup-heading">🔨 Working on today</div><div>{standup.today}</div></div>
              <div className="standup-section"><div className="standup-heading">🚧 Blockers</div><div>{standup.blockers}<span className="cursor"/></div></div>
            </>
          ):<div style={{color:"var(--text3)"}}>Click Generate New to create your standup</div>}
        </div>
        <div style={{display:"flex",gap:10}}>
          <button className="btn btn-ghost" style={{flex:1,justifyContent:"center"}} onClick={()=>standup&&navigator.clipboard.writeText(`✅ ${standup.did}\n🔨 ${standup.today}\n🚧 ${standup.blockers}`)}>📋 Copy to Clipboard</button>
          <button className="btn btn-ghost" style={{flex:1,justifyContent:"center"}} onClick={fetchStandup} disabled={standupLoading}>⟳ Regenerate</button>
        </div>
      </div>
      {standupHistory.length>0&&(
        <div className="panel">
          <div className="panel-header"><div className="panel-title">Standup History</div><span className="panel-badge badge-green">{standupHistory.length} saved</span></div>
          {standupHistory.map((h,i)=>(
            <div key={i} className="history-item">
              <div className="history-date">{new Date(h.generated_at).toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'short'})}</div>
              <div className="history-text">✅ {h.did}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const PageWellbeing = () => (
    <>
      <div className="page-header"><div><div className="page-title">Wellbeing</div><div className="page-sub">Your burnout risk and mental health as a developer</div></div></div>
      <div className="stats-grid-2">
        <div className="stat-card green">
          <div className="stat-label">Burnout Risk Score</div>
          <div className="stat-value" style={{color:getBurnoutColor(stats?.burnout_score||0)}}>{stats?.burnout_score||0}<span style={{fontSize:16,color:"var(--text3)"}}>/100</span></div>
          <div className="stat-change up">{getBurnoutLabel(stats?.burnout_score||0)}</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Commits This Period</div>
          <div className="stat-value">{stats?.commit_count||0}</div>
          <div className="stat-change neutral">Across {stats?.repos?.length||0} repos</div>
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Burnout Risk Gauge</div><span className="panel-badge badge-green">AI Monitored</span></div>
        <div style={{marginBottom:16}}>
          <div className="burnout-label-row"><span style={{fontSize:13,color:"var(--text2)"}}>Current Risk Level</span><span className="burnout-score" style={{color:getBurnoutColor(stats?.burnout_score||0)}}>{stats?.burnout_score||0}/100</span></div>
          <div className="burnout-bar-bg" style={{height:10}}><div className="burnout-bar-fill" style={{width:`${burnoutWidth}%`}}/></div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
            <span style={{fontSize:10,color:"var(--green)"}}>Safe</span>
            <span style={{fontSize:10,color:"var(--amber)"}}>Caution</span>
            <span style={{fontSize:10,color:"#f87171"}}>Danger</span>
          </div>
        </div>
        <div style={{background:"var(--bg3)",borderRadius:10,padding:16,border:"1px solid var(--border)"}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:8}}>💡 CodexPulse Recommendations</div>
          {(stats?.burnout_score||0)<40&&<div style={{fontSize:12,color:"var(--text2)",lineHeight:1.7}}>✅ You're coding at a healthy pace.<br/>✅ Great balance between activity and rest.<br/>✅ Keep up the consistent commit rhythm!</div>}
          {(stats?.burnout_score||0)>=40&&(stats?.burnout_score||0)<70&&<div style={{fontSize:12,color:"var(--text2)",lineHeight:1.7}}>⚠️ Your commit rate is getting high.<br/>💤 Make sure you're taking proper breaks.<br/>🎯 Focus on quality over quantity.</div>}
          {(stats?.burnout_score||0)>=70&&<div style={{fontSize:12,color:"var(--text2)",lineHeight:1.7}}>🔴 High risk — you may be overworking.<br/>🛑 Consider taking a day off.<br/>💬 Talk to your team about workload.</div>}
        </div>
      </div>
    </>
  );

  const PageCareer = () => (
    <>
      <div className="page-header"><div><div className="page-title">Career</div><div className="page-sub">Your developer growth and market position</div></div></div>
      <div className="career-grid">
        <div className="career-card"><div className="career-card-title">Public Repos</div><div className="career-card-value">{stats?.total_repos||0}</div><div className="career-card-sub">Your portfolio size</div></div>
        <div className="career-card"><div className="career-card-title">Top Language</div><div className="career-card-value" style={{fontSize:16}}>{stats?.top_language||"—"}</div><div className="career-card-sub">Most used in your repos</div></div>
        <div className="career-card"><div className="career-card-title">Active Projects</div><div className="career-card-value">{stats?.repos?.length||0}</div><div className="career-card-sub">Recently updated</div></div>
        <div className="career-card"><div className="career-card-title">Commit Velocity</div><div className="career-card-value">{stats?.commit_count||0}</div><div className="career-card-sub">Recent commits tracked</div></div>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">🚀 Career Recommendations</div><span className="panel-badge badge-green">AI Powered</span></div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[
            {icon:"🎯",title:"Strengthen your Python skills",desc:"You have Python projects — deepen your knowledge with FastAPI and ML libraries to increase market value."},
            {icon:"☁️",title:"Azure certification",desc:"You're already using Azure. An AZ-900 certification would boost your profile significantly."},
            {icon:"🌟",title:"Open source contributions",desc:`With ${stats?.total_repos||0} repos, consider making some public and contributing to popular projects.`},
            {icon:"📝",title:"Document your projects",desc:"Add README files to your repos — recruiters and collaborators look for well-documented projects."},
          ].map((tip,i)=>(
            <div key={i} style={{background:"var(--bg3)",borderRadius:10,padding:14,border:"1px solid var(--border)",display:"flex",gap:12,alignItems:"flex-start"}}>
              <span style={{fontSize:20,flexShrink:0}}>{tip.icon}</span>
              <div><div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:3}}>{tip.title}</div><div style={{fontSize:11,color:"var(--text2)",lineHeight:1.6}}>{tip.desc}</div></div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const PageIntegrations = () => (
    <>
      <div className="page-header"><div><div className="page-title">Integrations</div><div className="page-sub">Connect your tools to CodexPulse</div></div></div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Connected</div><span className="panel-badge badge-green">Active</span></div>
        <div className="integration-grid">
          {[{icon:"🐙",name:"GitHub",desc:"Commits, repos, activity — public & private"},{icon:"🤖",name:"Claude AI",desc:"Standup & burnout AI"}].map((item,i)=>(
            <div key={i} className="integration-card">
              <div className="integration-icon">{item.icon}</div>
              <div><div className="integration-name">{item.name}</div><div className="integration-desc">{item.desc}</div></div>
              <span className="integration-status status-connected">Connected</span>
            </div>
          ))}
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Coming Soon</div><span className="panel-badge badge-amber">Next release</span></div>
        <div className="integration-grid">
          {[{icon:"📋",name:"Jira",desc:"Sprint tickets & tasks"},{icon:"⚡",name:"Linear",desc:"Issue tracking"},{icon:"💬",name:"Slack",desc:"Post standup automatically"},{icon:"🔷",name:"Azure DevOps",desc:"Pipeline & work items"},{icon:"📊",name:"Notion",desc:"Export your reports"},{icon:"🎯",name:"VS Code",desc:"Live coding time tracker"}].map((item,i)=>(
            <div key={i} className="integration-card">
              <div className="integration-icon">{item.icon}</div>
              <div><div className="integration-name">{item.name}</div><div className="integration-desc">{item.desc}</div></div>
              <span className="integration-status status-soon">Soon</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const PageSettings = () => (
    <>
      <div className="page-header"><div><div className="page-title">Settings</div><div className="page-sub">Manage your CodexPulse account</div></div></div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Profile</div></div>
        <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20}}>
          {profile?.avatar?<img src={profile.avatar} alt="avatar" style={{width:56,height:56,borderRadius:"50%",border:"2px solid var(--accent)"}}/>:<div className="avatar-fallback" style={{width:56,height:56,fontSize:20}}>{username[0]?.toUpperCase()}</div>}
          <div>
            <div style={{fontSize:16,fontWeight:700}}>{username}</div>
            <div style={{fontSize:12,color:getPlanColor(),marginTop:3}}>{getPlanLabel()}</div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {[{label:"GitHub Username",value:username},{label:"Public Repos",value:stats?.total_repos||0},{label:"Plan",value:getPlanLabel()},{label:"Top Language",value:stats?.top_language||"—"}].map((row,i)=>(
            <div key={i} style={{background:"var(--bg3)",borderRadius:8,padding:12,border:"1px solid var(--border)"}}>
              <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{row.label}</div>
              <div style={{fontSize:13,fontWeight:600,fontFamily:"Space Mono,monospace"}}>{String(row.value)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><div className="panel-title">Privacy & Repositories</div></div>
        <div style={{background:"rgba(45,212,160,0.08)",border:"1px solid rgba(45,212,160,0.2)",borderRadius:10,padding:"12px 14px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:16,flexShrink:0}}>🛡️</span>
          <div style={{fontSize:12,color:"var(--green)",lineHeight:1.6}}><strong>Private by design.</strong> Your commit data is used only to generate your personal insights. We never share it with employers, sell it, or use it for any other purpose.</div>
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",borderBottom:"1px solid var(--border)"}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>Manage repositories</div>
            <div style={{fontSize:11,color:"var(--text3)"}}>{selectedRepos.length>0?`Analysing ${selectedRepos.length} repo${selectedRepos.length>1?"s":""}:`:"Choose which repos to analyse"}{selectedRepos.length>0&&<span style={{color:"var(--accent2)",marginLeft:4}}>{selectedRepos.join(", ")}</span>}</div>
          </div>
          <button className="btn btn-ghost" onClick={async()=>{await fetchAvailableRepos();setShowRepoSelector(true);}}>🔒 Change repos</button>
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0"}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>Sign out of CodexPulse</div>
            <div style={{fontSize:11,color:"var(--text3)"}}>You'll need to login with GitHub again</div>
          </div>
          <button className="btn btn-danger" onClick={handleLogout}>Logout</button>
        </div>
      </div>
    </>
  );

  const renderPage = () => {
    switch(activeNav) {
      case "Activity": return <PageActivity/>;
      case "Repos": return <PageRepos/>;
      case "Standup": return <PageStandup/>;
      case "Wellbeing": return <PageWellbeing/>;
      case "Career": return <PageCareer/>;
      case "Integrations": return <PageIntegrations/>;
      case "Settings": return <PageSettings/>;
      default: return <PageDashboard/>;
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="shell">
        <aside className="sidebar">
          <div className="logo">
            <div className="logo-mark">
              <div className="logo-dot"/>
              <span style={{fontFamily:"Space Mono,monospace",fontSize:14,fontWeight:700}}>CodexPulse</span>
            </div>
          </div>
          {navGroups.map(group=>(
            <div key={group.section}>
              <div className="nav-section">{group.section}</div>
              {group.items.map(item=>(
                <div key={item.label} className={`nav-item ${activeNav===item.label?"active":""}`} onClick={()=>setActiveNav(item.label)}>
                  <span>{item.icon}</span>{item.label}
                </div>
              ))}
            </div>
          ))}
          <div className="sidebar-footer">
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              {profile?.avatar?<img src={profile.avatar} alt="avatar" className="avatar-img"/>:<div className="avatar-fallback">{username[0]?.toUpperCase()}</div>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{username}</div>
                <div style={{fontSize:11,color:getPlanColor()}}>{getPlanLabel()}</div>
              </div>
              <button className="btn btn-ghost" style={{padding:"4px 8px",fontSize:11}} onClick={handleLogout}>↪</button>
            </div>
          </div>
        </aside>
        <main className="main">
          {error&&<div className="error-box">⚠️ {error}</div>}
          {renderPage()}
        </main>
      </div>
    </>
  );
}